/**
 * KpiCardControl
 * Displays key performance indicator (KPI) metric card
 * Shows value, unit, trend, and description
 * Performance: Memoized to prevent unnecessary re-renders
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../types';

const KpiCardControl = memo<ControlRendererProps>(({ control, value, mode }) => {
  const title = (control.config?.title as string) || control.label || 'KPI';
  const unit = (control.config?.unit as string) || '';
  const trend = (control.config?.trend as 'up' | 'down' | 'neutral') || 'neutral';
  const trendValue = (control.config?.trendValue as string) || '';
  const description = (control.config?.description as string) || '';
  const backgroundColor = (control.config?.color as string) || '#3b82f6';

  const displayValue = useMemo(() => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') return value.toLocaleString();
    return String(value);
  }, [value]);

  const trendIcon = useMemo(() => {
    if (trend === 'up') return '↑';
    if (trend === 'down') return '↓';
    return '→';
  }, [trend]);

  const trendColor = useMemo(() => {
    if (trend === 'up') return '#22c55e';
    if (trend === 'down') return '#ef4444';
    return '#94a3b8';
  }, [trend]);

  if (mode === 'view') {
    return (
      <div className="af-kpi-card__container af-kpi-card--view">
        <div className="af-kpi-card__title">{title}</div>
        <div className="af-kpi-card__value-group">
          <span className="af-kpi-card__value">{displayValue}</span>
          {unit && <span className="af-kpi-card__unit">{unit}</span>}
        </div>
        {description && <div className="af-kpi-card__description">{description}</div>}
      </div>
    );
  }

  return (
    <div className="af-kpi-card__container" style={{ borderLeftColor: backgroundColor }}>
      <div className="af-kpi-card__header">
        <div className="af-kpi-card__title">{title}</div>
        {trendValue && (
          <div className="af-kpi-card__trend" style={{ color: trendColor }}>
            <span className="af-kpi-card__trend-icon">{trendIcon}</span>
            <span className="af-kpi-card__trend-value">{trendValue}</span>
          </div>
        )}
      </div>

      <div className="af-kpi-card__value-group">
        <span className="af-kpi-card__value">{displayValue}</span>
        {unit && <span className="af-kpi-card__unit">{unit}</span>}
      </div>

      {description && <div className="af-kpi-card__description">{description}</div>}
    </div>
  );
});

KpiCardControl.displayName = 'KpiCardControl';

export default KpiCardControl;
